// Recipe database for the weekly meal planner. Ingredients carry a store
// category so the generated shopping list can be grouped by aisle. Quantities
// are per recipe (a normal household portion); combine by name + unit.

export type Cat =
  | "Frukt & grönt"
  | "Kött & fisk"
  | "Mejeri & ägg"
  | "Bröd"
  | "Fryst"
  | "Skafferi"
  | "Övrigt";

// Order the shopping list follows (roughly a store walk-through).
export const CAT_ORDER: Cat[] = [
  "Frukt & grönt",
  "Kött & fisk",
  "Mejeri & ägg",
  "Bröd",
  "Fryst",
  "Skafferi",
  "Övrigt",
];

export interface Ingredient {
  name: string;
  qty: number;
  unit: string; // g, kg, st, dl, l, msk, tsk, förp, burk, knippe…
  cat: Cat;
}

// Primary food type — drives the taste preferences filter.
export type FoodKind = "meat" | "fish" | "veg";
export const KIND_META: Record<FoodKind, { label: string; icon: string }> = {
  meat: { label: "Kött", icon: "🥩" },
  fish: { label: "Fisk", icon: "🐟" },
  veg: { label: "Vegetariskt", icon: "🥗" },
};
export const KIND_ORDER: FoodKind[] = ["meat", "fish", "veg"];

export interface Recipe {
  id: string;
  name: string;
  emoji: string;
  kind: FoodKind;
  matlada: boolean; // reheats well / good for lunch-box meal prep
  servings: number;
  tags: string[];
  ingredients: Ingredient[];
}

const i = (name: string, qty: number, unit: string, cat: Cat): Ingredient => ({ name, qty, unit, cat });

export const RECIPES: Recipe[] = [
  {
    id: "bolognese", name: "Spaghetti Bolognese", emoji: "🍝", kind: "meat", matlada: true, servings: 4, tags: ["Barnvänlig"],
    ingredients: [
      i("Spaghetti", 400, "g", "Skafferi"),
      i("Nötfärs", 500, "g", "Kött & fisk"),
      i("Krossade tomater", 1, "burk", "Skafferi"),
      i("Gul lök", 1, "st", "Frukt & grönt"),
      i("Vitlöksklyfta", 2, "st", "Frukt & grönt"),
      i("Morot", 1, "st", "Frukt & grönt"),
      i("Parmesan", 1, "förp", "Mejeri & ägg"),
    ],
  },
  {
    id: "tacos", name: "Tacos", emoji: "🌮", kind: "meat", matlada: false, servings: 4, tags: ["Barnvänlig", "Fredagsmys"],
    ingredients: [
      i("Tortillabröd", 1, "förp", "Bröd"),
      i("Nötfärs", 500, "g", "Kött & fisk"),
      i("Tacokrydda", 1, "förp", "Skafferi"),
      i("Majs", 1, "burk", "Skafferi"),
      i("Paprika", 1, "st", "Frukt & grönt"),
      i("Tomat", 2, "st", "Frukt & grönt"),
      i("Riven ost", 1, "förp", "Mejeri & ägg"),
      i("Gräddfil", 1, "förp", "Mejeri & ägg"),
      i("Sallad", 1, "st", "Frukt & grönt"),
    ],
  },
  {
    id: "lax", name: "Ugnslax med potatis", emoji: "🐟", kind: "fish", matlada: true, servings: 4, tags: ["Fisk"],
    ingredients: [
      i("Laxfilé", 600, "g", "Kött & fisk"),
      i("Potatis", 1, "kg", "Frukt & grönt"),
      i("Citron", 1, "st", "Frukt & grönt"),
      i("Broccoli", 1, "st", "Frukt & grönt"),
      i("Dill", 1, "knippe", "Frukt & grönt"),
      i("Smör", 1, "förp", "Mejeri & ägg"),
    ],
  },
  {
    id: "kycklinggryta", name: "Kycklinggryta med ris", emoji: "🍗", kind: "meat", matlada: true, servings: 4, tags: [],
    ingredients: [
      i("Kycklingfilé", 600, "g", "Kött & fisk"),
      i("Ris", 4, "dl", "Skafferi"),
      i("Kokosmjölk", 1, "burk", "Skafferi"),
      i("Currypasta", 1, "förp", "Skafferi"),
      i("Paprika", 1, "st", "Frukt & grönt"),
      i("Gul lök", 1, "st", "Frukt & grönt"),
      i("Vitlöksklyfta", 2, "st", "Frukt & grönt"),
    ],
  },
  {
    id: "veg-chili", name: "Vegetarisk chili", emoji: "🌶️", kind: "veg", matlada: true, servings: 4, tags: ["Veg"],
    ingredients: [
      i("Kidneybönor", 1, "burk", "Skafferi"),
      i("Svarta bönor", 1, "burk", "Skafferi"),
      i("Krossade tomater", 1, "burk", "Skafferi"),
      i("Majs", 1, "burk", "Skafferi"),
      i("Gul lök", 1, "st", "Frukt & grönt"),
      i("Paprika", 1, "st", "Frukt & grönt"),
      i("Ris", 4, "dl", "Skafferi"),
    ],
  },
  {
    id: "kottbullar", name: "Köttbullar med potatismos", emoji: "🍽️", kind: "meat", matlada: true, servings: 4, tags: ["Barnvänlig"],
    ingredients: [
      i("Köttbullar", 1, "förp", "Fryst"),
      i("Potatis", 1, "kg", "Frukt & grönt"),
      i("Mjölk", 1, "l", "Mejeri & ägg"),
      i("Smör", 1, "förp", "Mejeri & ägg"),
      i("Grädde", 1, "förp", "Mejeri & ägg"),
      i("Lingonsylt", 1, "burk", "Skafferi"),
    ],
  },
  {
    id: "pannkakor", name: "Pannkakor", emoji: "🥞", kind: "veg", matlada: false, servings: 4, tags: ["Veg", "Barnvänlig"],
    ingredients: [
      i("Vetemjöl", 1, "kg", "Skafferi"),
      i("Mjölk", 1, "l", "Mejeri & ägg"),
      i("Ägg", 6, "st", "Mejeri & ägg"),
      i("Smör", 1, "förp", "Mejeri & ägg"),
      i("Sylt", 1, "burk", "Skafferi"),
    ],
  },
  {
    id: "pytt", name: "Pytt i panna", emoji: "🍳", kind: "meat", matlada: true, servings: 4, tags: ["Snabb"],
    ingredients: [
      i("Pytt i panna", 1, "förp", "Fryst"),
      i("Ägg", 4, "st", "Mejeri & ägg"),
      i("Rödbetor", 1, "burk", "Skafferi"),
      i("Ketchup", 1, "st", "Skafferi"),
    ],
  },
  {
    id: "fiskgratang", name: "Fiskgratäng", emoji: "🐟", kind: "fish", matlada: true, servings: 4, tags: ["Fisk"],
    ingredients: [
      i("Vit fisk (torsk/sej)", 600, "g", "Kött & fisk"),
      i("Potatis", 1, "kg", "Frukt & grönt"),
      i("Grädde", 1, "förp", "Mejeri & ägg"),
      i("Riven ost", 1, "förp", "Mejeri & ägg"),
      i("Ärtor", 1, "förp", "Fryst"),
      i("Dill", 1, "knippe", "Frukt & grönt"),
    ],
  },
  {
    id: "wok", name: "Kycklingwok med nudlar", emoji: "🍜", kind: "meat", matlada: true, servings: 4, tags: ["Snabb"],
    ingredients: [
      i("Äggnudlar", 1, "förp", "Skafferi"),
      i("Kycklingfilé", 500, "g", "Kött & fisk"),
      i("Wokgrönsaker", 1, "förp", "Fryst"),
      i("Sojasås", 1, "st", "Skafferi"),
      i("Ingefära", 1, "st", "Frukt & grönt"),
      i("Vitlöksklyfta", 2, "st", "Frukt & grönt"),
    ],
  },
  {
    id: "halloumi", name: "Halloumisallad", emoji: "🥗", kind: "veg", matlada: false, servings: 4, tags: ["Veg", "Snabb"],
    ingredients: [
      i("Halloumi", 2, "förp", "Mejeri & ägg"),
      i("Sallad", 1, "st", "Frukt & grönt"),
      i("Tomat", 3, "st", "Frukt & grönt"),
      i("Gurka", 1, "st", "Frukt & grönt"),
      i("Avokado", 2, "st", "Frukt & grönt"),
      i("Bröd", 1, "st", "Bröd"),
    ],
  },
  {
    id: "korv-stroganoff", name: "Korv Stroganoff", emoji: "🌭", kind: "meat", matlada: true, servings: 4, tags: ["Barnvänlig", "Snabb"],
    ingredients: [
      i("Falukorv", 1, "st", "Kött & fisk"),
      i("Ris", 4, "dl", "Skafferi"),
      i("Krossade tomater", 1, "burk", "Skafferi"),
      i("Grädde", 1, "förp", "Mejeri & ägg"),
      i("Gul lök", 1, "st", "Frukt & grönt"),
      i("Tomatpuré", 1, "st", "Skafferi"),
    ],
  },
  {
    id: "ugnskyckling", name: "Ugnskyckling med rotfrukter", emoji: "🍗", kind: "meat", matlada: true, servings: 4, tags: [],
    ingredients: [
      i("Kycklinglår", 8, "st", "Kött & fisk"),
      i("Potatis", 1, "kg", "Frukt & grönt"),
      i("Morot", 3, "st", "Frukt & grönt"),
      i("Palsternacka", 2, "st", "Frukt & grönt"),
      i("Vitlöksklyfta", 3, "st", "Frukt & grönt"),
      i("Olivolja", 1, "st", "Skafferi"),
    ],
  },
  {
    id: "linsgryta", name: "Röd linsgryta", emoji: "🫕", kind: "veg", matlada: true, servings: 4, tags: ["Veg"],
    ingredients: [
      i("Röda linser", 3, "dl", "Skafferi"),
      i("Kokosmjölk", 1, "burk", "Skafferi"),
      i("Krossade tomater", 1, "burk", "Skafferi"),
      i("Gul lök", 1, "st", "Frukt & grönt"),
      i("Spenat", 1, "förp", "Frukt & grönt"),
      i("Ris", 4, "dl", "Skafferi"),
    ],
  },
  {
    id: "hamburgare", name: "Hamburgare", emoji: "🍔", kind: "meat", matlada: false, servings: 4, tags: ["Fredagsmys"],
    ingredients: [
      i("Hamburgerbröd", 1, "förp", "Bröd"),
      i("Nötfärs", 500, "g", "Kött & fisk"),
      i("Ost i skivor", 1, "förp", "Mejeri & ägg"),
      i("Sallad", 1, "st", "Frukt & grönt"),
      i("Tomat", 2, "st", "Frukt & grönt"),
      i("Röd lök", 1, "st", "Frukt & grönt"),
      i("Pommes", 1, "förp", "Fryst"),
    ],
  },
  {
    id: "rakpasta", name: "Räkpasta", emoji: "🍤", kind: "fish", matlada: true, servings: 4, tags: ["Fisk", "Snabb"],
    ingredients: [
      i("Pasta", 400, "g", "Skafferi"),
      i("Räkor", 400, "g", "Fryst"),
      i("Grädde", 1, "förp", "Mejeri & ägg"),
      i("Vitlöksklyfta", 2, "st", "Frukt & grönt"),
      i("Citron", 1, "st", "Frukt & grönt"),
      i("Spenat", 1, "förp", "Frukt & grönt"),
      i("Parmesan", 1, "förp", "Mejeri & ägg"),
    ],
  },
  {
    id: "omelett", name: "Omelett med sallad", emoji: "🍳", kind: "veg", matlada: false, servings: 4, tags: ["Veg", "Snabb"],
    ingredients: [
      i("Ägg", 8, "st", "Mejeri & ägg"),
      i("Riven ost", 1, "förp", "Mejeri & ägg"),
      i("Sallad", 1, "st", "Frukt & grönt"),
      i("Tomat", 2, "st", "Frukt & grönt"),
      i("Bröd", 1, "st", "Bröd"),
    ],
  },
  {
    id: "tomatsoppa", name: "Tomatsoppa med mackor", emoji: "🍅", kind: "veg", matlada: true, servings: 4, tags: ["Veg", "Snabb"],
    ingredients: [
      i("Krossade tomater", 2, "burk", "Skafferi"),
      i("Grädde", 1, "förp", "Mejeri & ägg"),
      i("Gul lök", 1, "st", "Frukt & grönt"),
      i("Vitlöksklyfta", 2, "st", "Frukt & grönt"),
      i("Bröd", 1, "st", "Bröd"),
      i("Ost", 1, "förp", "Mejeri & ägg"),
    ],
  },
  {
    id: "pad-thai", name: "Pad thai", emoji: "🍜", kind: "meat", matlada: true, servings: 4, tags: ["Snabb"],
    ingredients: [
      i("Risnudlar", 1, "förp", "Skafferi"),
      i("Kycklingfilé", 400, "g", "Kött & fisk"),
      i("Ägg", 3, "st", "Mejeri & ägg"),
      i("Morot", 1, "st", "Frukt & grönt"),
      i("Salladslök", 1, "knippe", "Frukt & grönt"),
      i("Böngroddar", 1, "förp", "Frukt & grönt"),
      i("Lime", 1, "st", "Frukt & grönt"),
      i("Jordnötter", 1, "förp", "Skafferi"),
      i("Sojasås", 1, "st", "Skafferi"),
      i("Sweet chilisås", 1, "st", "Skafferi"),
    ],
  },
  {
    id: "teriyaki-quorn", name: "Teriyaki med nudlar & Quorn", emoji: "🍜", kind: "veg", matlada: true, servings: 4, tags: ["Veg"],
    ingredients: [
      i("Äggnudlar", 1, "förp", "Skafferi"),
      i("Quornbitar", 1, "förp", "Fryst"),
      i("Teriyakisås", 1, "st", "Skafferi"),
      i("Broccoli", 1, "st", "Frukt & grönt"),
      i("Morot", 2, "st", "Frukt & grönt"),
      i("Salladslök", 1, "knippe", "Frukt & grönt"),
      i("Sesamfrön", 1, "förp", "Skafferi"),
    ],
  },
  {
    id: "kottbullar-makaroner", name: "Köttbullar & makaroner", emoji: "🍽️", kind: "meat", matlada: true, servings: 4, tags: ["Barnvänlig"],
    ingredients: [
      i("Köttbullar", 1, "förp", "Fryst"),
      i("Makaroner", 500, "g", "Skafferi"),
      i("Grädde", 1, "förp", "Mejeri & ägg"),
      i("Ketchup", 1, "st", "Skafferi"),
    ],
  },
  {
    id: "salsiccia-pasta", name: "Salsiccia pasta", emoji: "🍝", kind: "meat", matlada: true, servings: 4, tags: [],
    ingredients: [
      i("Salsicciakorv", 400, "g", "Kött & fisk"),
      i("Penne", 400, "g", "Skafferi"),
      i("Grädde", 1, "förp", "Mejeri & ägg"),
      i("Körsbärstomater", 1, "förp", "Frukt & grönt"),
      i("Gul lök", 1, "st", "Frukt & grönt"),
      i("Vitlöksklyfta", 2, "st", "Frukt & grönt"),
      i("Spenat", 1, "förp", "Frukt & grönt"),
      i("Parmesan", 1, "förp", "Mejeri & ägg"),
    ],
  },
  {
    id: "pasta-pesto-quorn", name: "Pasta pesto & Quorn", emoji: "🌿", kind: "veg", matlada: true, servings: 4, tags: ["Veg", "Snabb"],
    ingredients: [
      i("Pasta", 400, "g", "Skafferi"),
      i("Quornbitar", 1, "förp", "Fryst"),
      i("Pesto", 1, "burk", "Skafferi"),
      i("Crème fraiche", 1, "förp", "Mejeri & ägg"),
      i("Körsbärstomater", 1, "förp", "Frukt & grönt"),
      i("Spenat", 1, "förp", "Frukt & grönt"),
      i("Parmesan", 1, "förp", "Mejeri & ägg"),
    ],
  },
  {
    id: "kottfarslimpa", name: "Köttfärslimpa med rotfrukter", emoji: "🍖", kind: "meat", matlada: true, servings: 4, tags: [],
    ingredients: [
      i("Nötfärs", 500, "g", "Kött & fisk"),
      i("Ägg", 1, "st", "Mejeri & ägg"),
      i("Ströbröd", 1, "förp", "Skafferi"),
      i("Gul lök", 1, "st", "Frukt & grönt"),
      i("Potatis", 1, "kg", "Frukt & grönt"),
      i("Morot", 3, "st", "Frukt & grönt"),
      i("Palsternacka", 2, "st", "Frukt & grönt"),
      i("Grädde", 1, "förp", "Mejeri & ägg"),
    ],
  },
  {
    id: "tikka-masala", name: "Kyckling tikka masala", emoji: "🍛", kind: "meat", matlada: true, servings: 4, tags: [],
    ingredients: [
      i("Kycklingfilé", 600, "g", "Kött & fisk"),
      i("Tikka masala-sås", 1, "burk", "Skafferi"),
      i("Kokosmjölk", 1, "burk", "Skafferi"),
      i("Ris", 4, "dl", "Skafferi"),
      i("Gul lök", 1, "st", "Frukt & grönt"),
      i("Naanbröd", 1, "förp", "Bröd"),
    ],
  },
  {
    id: "carbonara", name: "Pasta carbonara", emoji: "🍝", kind: "meat", matlada: true, servings: 4, tags: ["Snabb"],
    ingredients: [
      i("Spaghetti", 400, "g", "Skafferi"),
      i("Bacon", 140, "g", "Kött & fisk"),
      i("Ägg", 3, "st", "Mejeri & ägg"),
      i("Parmesan", 1, "förp", "Mejeri & ägg"),
      i("Grädde", 1, "förp", "Mejeri & ägg"),
    ],
  },
  {
    id: "flaskfile", name: "Fläskfilé med klyftpotatis", emoji: "🥩", kind: "meat", matlada: true, servings: 4, tags: [],
    ingredients: [
      i("Fläskfilé", 600, "g", "Kött & fisk"),
      i("Potatis", 1, "kg", "Frukt & grönt"),
      i("Grädde", 1, "förp", "Mejeri & ägg"),
      i("Dijonsenap", 1, "st", "Skafferi"),
      i("Ärtor", 1, "förp", "Fryst"),
    ],
  },
  {
    id: "ramen", name: "Ramen", emoji: "🍜", kind: "meat", matlada: true, servings: 4, tags: [],
    ingredients: [
      i("Ramennudlar", 1, "förp", "Skafferi"),
      i("Kycklingfilé", 400, "g", "Kött & fisk"),
      i("Ägg", 4, "st", "Mejeri & ägg"),
      i("Buljong", 1, "förp", "Skafferi"),
      i("Salladslök", 1, "knippe", "Frukt & grönt"),
      i("Morot", 1, "st", "Frukt & grönt"),
      i("Sojasås", 1, "st", "Skafferi"),
    ],
  },
  {
    id: "falafel", name: "Falafel med bulgur", emoji: "🧆", kind: "veg", matlada: true, servings: 4, tags: ["Veg"],
    ingredients: [
      i("Falafel", 1, "förp", "Fryst"),
      i("Bulgur", 3, "dl", "Skafferi"),
      i("Hummus", 1, "förp", "Skafferi"),
      i("Tomat", 2, "st", "Frukt & grönt"),
      i("Gurka", 1, "st", "Frukt & grönt"),
      i("Pitabröd", 1, "förp", "Bröd"),
    ],
  },
  {
    id: "fisktacos", name: "Fisktacos", emoji: "🌮", kind: "fish", matlada: false, servings: 4, tags: ["Fisk", "Fredagsmys"],
    ingredients: [
      i("Vit fisk (torsk/sej)", 500, "g", "Kött & fisk"),
      i("Tortillabröd", 1, "förp", "Bröd"),
      i("Vitkål", 1, "st", "Frukt & grönt"),
      i("Lime", 1, "st", "Frukt & grönt"),
      i("Avokado", 2, "st", "Frukt & grönt"),
      i("Majonnäs", 1, "st", "Skafferi"),
    ],
  },
  {
    id: "pannbiff", name: "Pannbiff med lök", emoji: "🍖", kind: "meat", matlada: true, servings: 4, tags: ["Barnvänlig"],
    ingredients: [
      i("Nötfärs", 500, "g", "Kött & fisk"),
      i("Gul lök", 2, "st", "Frukt & grönt"),
      i("Potatis", 1, "kg", "Frukt & grönt"),
      i("Grädde", 1, "förp", "Mejeri & ägg"),
      i("Gurka", 1, "st", "Frukt & grönt"),
    ],
  },
  {
    id: "grekisk-kyckling", name: "Grekisk sallad med kyckling", emoji: "🥗", kind: "meat", matlada: false, servings: 4, tags: ["Snabb"],
    ingredients: [
      i("Kycklingfilé", 500, "g", "Kött & fisk"),
      i("Sallad", 1, "st", "Frukt & grönt"),
      i("Tomat", 3, "st", "Frukt & grönt"),
      i("Gurka", 1, "st", "Frukt & grönt"),
      i("Fetaost", 1, "förp", "Mejeri & ägg"),
      i("Oliver", 1, "burk", "Skafferi"),
      i("Rödlök", 1, "st", "Frukt & grönt"),
      i("Bröd", 1, "st", "Bröd"),
    ],
  },
  {
    id: "veg-lasagne", name: "Vegetarisk lasagne", emoji: "🧀", kind: "veg", matlada: true, servings: 4, tags: ["Veg"],
    ingredients: [
      i("Lasagneplattor", 1, "förp", "Skafferi"),
      i("Sojafärs", 1, "förp", "Fryst"),
      i("Krossade tomater", 1, "burk", "Skafferi"),
      i("Riven ost", 1, "förp", "Mejeri & ägg"),
      i("Grädde", 1, "förp", "Mejeri & ägg"),
      i("Gul lök", 1, "st", "Frukt & grönt"),
      i("Morot", 2, "st", "Frukt & grönt"),
    ],
  },
  {
    id: "kyckling-fajitas", name: "Kycklingfajitas", emoji: "🌯", kind: "meat", matlada: false, servings: 4, tags: ["Fredagsmys"],
    ingredients: [
      i("Kycklingfilé", 600, "g", "Kött & fisk"),
      i("Tortillabröd", 1, "förp", "Bröd"),
      i("Paprika", 2, "st", "Frukt & grönt"),
      i("Gul lök", 1, "st", "Frukt & grönt"),
      i("Fajitakrydda", 1, "förp", "Skafferi"),
      i("Gräddfil", 1, "förp", "Mejeri & ägg"),
      i("Riven ost", 1, "förp", "Mejeri & ägg"),
    ],
  },
  {
    id: "artsoppa", name: "Ärtsoppa med fläsk", emoji: "🟡", kind: "meat", matlada: true, servings: 4, tags: [],
    ingredients: [
      i("Gula ärtor", 5, "dl", "Skafferi"),
      i("Fläsklägg", 400, "g", "Kött & fisk"),
      i("Gul lök", 1, "st", "Frukt & grönt"),
      i("Senap", 1, "st", "Skafferi"),
    ],
  },
];

export const recipeById = (id: string): Recipe | undefined => RECIPES.find((r) => r.id === id);

const shuffle = <T,>(arr: T[]): T[] => {
  const a = [...arr];
  for (let k = a.length - 1; k > 0; k--) {
    const j = Math.floor(Math.random() * (k + 1));
    [a[k], a[j]] = [a[j], a[k]];
  }
  return a;
};

// Pick `n` distinct random recipes from `source`, honouring the liked food kinds
// (empty = all) and biasing toward favourites (they're offered first).
export const pickRandom = (
  source: Recipe[],
  n: number,
  opts: { exclude?: string[]; kinds?: FoodKind[]; favorites?: string[]; matladaOnly?: boolean } = {}
): Recipe[] => {
  const { exclude = [], kinds, favorites = [], matladaOnly = false } = opts;
  const allowed = kinds && kinds.length ? new Set(kinds) : null;
  const pool = source.filter(
    (r) => !exclude.includes(r.id) && (!allowed || allowed.has(r.kind)) && (!matladaOnly || r.matlada)
  );
  const favSet = new Set(favorites);
  const favs = shuffle(pool.filter((r) => favSet.has(r.id)));
  const rest = shuffle(pool.filter((r) => !favSet.has(r.id)));
  return [...favs, ...rest].slice(0, Math.min(n, pool.length));
};

// ---- editing support ----
export const UNITS = ["st", "g", "kg", "dl", "l", "förp", "burk", "knippe", "klyfta", "msk", "tsk"];
export const BUILTIN_IDS = new Set(RECIPES.map((r) => r.id));
export const newRecipeId = (): string =>
  "custom-" + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
export const blankRecipe = (): Recipe => ({
  id: newRecipeId(),
  name: "",
  emoji: "🍽️",
  kind: "meat",
  matlada: true,
  servings: 4,
  tags: [],
  ingredients: [{ name: "", qty: 1, unit: "st", cat: "Övrigt" }],
});

export interface ShopItem { name: string; qty: number; unit: string; cat: Cat; key: string }

// A planned meal: a recipe cooked for a chosen number of servings.
export interface PlanMeal { recipe: Recipe; servings: number }

// Whole units that don't make sense as fractions → round up when scaling.
const DISCRETE_UNITS = new Set(["st", "burk", "förp", "knippe", "klyfta"]);
const tidy = (q: number, unit: string): number =>
  DISCRETE_UNITS.has(unit) ? Math.ceil(q - 1e-9) : Math.round(q * 10) / 10;

// Rough "how much to cook" rule of thumb: ~167 g of the main ingredient per
// portion/matlåda (the user's 1 kg mince ≈ 6 boxes).
export const GRAMS_PER_PORTION = 167;

const grams = (ing: Ingredient): number =>
  ing.unit === "kg" ? ing.qty * 1000 : ing.unit === "g" ? ing.qty : 0;

// The ingredient you batch-cook: the heaviest protein (Kött & fisk), else the
// heaviest weighed ingredient. Null when nothing in the recipe is by weight.
export const mainIngredient = (recipe: Recipe): Ingredient | null => {
  const weighed = recipe.ingredients.filter((ing) => grams(ing) > 0);
  if (!weighed.length) return null;
  const protein = weighed.filter((ing) => ing.cat === "Kött & fisk");
  const pool = protein.length ? protein : weighed;
  return pool.reduce((a, b) => (grams(b) > grams(a) ? b : a));
};

// Below this per-portion weight a "protein" is really a flavouring (bacon in
// carbonara), so the thumb rule would wildly overshoot — fall back to the recipe.
const BULK_MIN_G = 70;

const fmtGrams = (g: number): string => {
  const r = Math.round(g / 50) * 50;
  return r >= 1000
    ? `${(r / 1000).toFixed(1).replace(/\.0$/, "").replace(".", ",")} kg`
    : `${r} g`;
};

// Estimated amount of the main ingredient to cook for `servings` portions.
// For a real meat/fish main we use the thumb rule (~167 g/portion → 1 kg ≈ 6
// boxes); otherwise we scale the recipe's own amount so flavourings stay sane.
export const cookEstimate = (recipe: Recipe, servings: number): { name: string; amount: string } | null => {
  const main = mainIngredient(recipe);
  if (!main) return null;
  const perPortion = grams(main) / (recipe.servings || 1);
  const isBulkProtein = main.cat === "Kött & fisk" && perPortion >= BULK_MIN_G;
  const g = isBulkProtein
    ? servings * GRAMS_PER_PORTION
    : grams(main) * (servings / (recipe.servings || 1));
  return { name: main.name.toLowerCase(), amount: fmtGrams(g) };
};

// Aggregate the ingredients of the planned meals into a shopping list, grouped
// by category. Each recipe's quantities are scaled by servings / base servings,
// then same name + unit are summed.
export const buildShoppingList = (meals: PlanMeal[]): { cat: Cat; items: ShopItem[] }[] => {
  const map = new Map<string, ShopItem>();
  meals.forEach(({ recipe, servings }) => {
    const mult = servings / (recipe.servings || 1);
    recipe.ingredients.forEach((ing) => {
      const key = `${ing.name}|${ing.unit}`;
      const existing = map.get(key);
      if (existing) existing.qty += ing.qty * mult;
      else map.set(key, { name: ing.name, qty: ing.qty * mult, unit: ing.unit, cat: ing.cat, key });
    });
  });
  return CAT_ORDER.map((cat) => ({
    cat,
    items: Array.from(map.values())
      .filter((it) => it.cat === cat)
      .map((it) => ({ ...it, qty: tidy(it.qty, it.unit) }))
      .sort((a, b) => a.name.localeCompare(b.name, "sv")),
  })).filter((g) => g.items.length > 0);
};
